/**
 * Page Point de Vente (POS) - Version Modulaire
 * 
 * Cette page charge dynamiquement le layout approprié selon le type d'activité :
 * - Retail Layout : Pour épicerie, vêtements, électronique, boulangerie
 * - Restaurant Layout : Pour restaurant, café, pâtisserie
 * - Services Layout : Pour coiffure, réparation, auto-école
 * - Workshop Layout : Pour atelier, freelance, services ponctuels
 * 
 * @component
 * @returns {JSX.Element} Composant Pos
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { pos as frPos } from '../../../../../locales/fr/pos';
import { pos as esPos } from '../../../../../locales/es/pos';
import { pos as arPos } from '../../../../../locales/ar/pos';
import useMyAppStore from '../../../../../stores/myAppStore';
import { SaleService } from '../../../../../services/AppDataServices/services/sale.service';
import FloatingAlert from '../../../../../components/Common/FloatingAlert';

// Layouts disponibles
import RetailLayout from './layouts/RetailLayout';
import RestaurantLayout from './layouts/RestaurantLayout';
import ServicesLayout from './layouts/ServicesLayout';
import WorkshopLayout from './layouts/WorkshopLayout';

const translations = {
  fr: frPos,
  es: esPos,
  ar: arPos
};

const Pos = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { setPageTitle, selectedLayout, currentActivity, currentUser } = useMyAppStore();

  // ===== ÉTATS DE BASE POUR LA GESTION DES COMMANDES =====
  
  // Structure d'une commande initiale
  const getInitialCommande = () => ({
    id: null,
    num: null,
    date: new Date(),
    sens: 1,
    customerId: 1, // Client comptant par défaut
    customer: {
      name: "Client Comptant",
      phone: "0689012345",
      address: "42 boulevard des Saveurs"
    },
    items: [],
    montant: {
      total: 0,
      discount: 0,
      marge: 0,
      tva: 0,
      ht: 0
    },
    paiement: {
      paymentId: null,
      datePaiement: null,
      method: "cash",
      status: "pending",
      amount: 0,
      rest: 0,
      note: ""
    },
    status: "draft",
    notes: "",
    userId: currentUser?.id || null, // Utiliser l'ID de l'utilisateur connecté ou null
    createdAt: new Date(),
    updatedAt: new Date(),
    lastSync: new Date()
  });
  
  // 1. Liste des commandes (ventes en attente) - Initialisée avec une commande
  const [commandes, setCommandes] = useState([getInitialCommande()]);
  
  // 2. Index de la commande courante (au lieu de la commande complète)
  const [currentCommandeIndex, setCurrentCommandeIndex] = useState(0);
  
  // 3. Ligne sélectionnée dans la commande courante (pour le keypad)
  const [selectedLineIndex, setSelectedLineIndex] = useState(null);

  // État pour les notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success'
  });

  // Getter pour obtenir la commande courante
  const currentCommande = commandes[currentCommandeIndex];

  // ===== FONCTIONS DE BASE À IMPLÉMENTER =====
  
  // 4. Fonction pour ajouter un produit à la commande courante
  const addProductToCurrentCommande = (product, quantity = 1) => {     
    
    // Vérifier que le produit est valide
    if (!product || !product.id) {
      console.error('❌ Produit invalide:', product);
      return;
    }

    // Vérifier que la quantité est positive
    if (quantity <= 0) {
      console.error('❌ Quantité invalide:', quantity);
      return;
    }

    setCommandes(prevCommandes => {
      // Copier le tableau des commandes
      const updatedCommandes = [...prevCommandes];
    
      // Cloner en profondeur la commande actuelle pour éviter toute mutation
      const oldCommande = updatedCommandes[currentCommandeIndex];
      const currentCommande = {
        ...oldCommande,
        items: [...oldCommande.items]
      };
    
      // Remplacer la commande par la copie dans le tableau
      updatedCommandes[currentCommandeIndex] = currentCommande;
    
      // Chercher si le produit existe déjà dans la commande
      const existingProductIndex = currentCommande.items.findIndex(
        item => item.productId === product.id
      );
          
      if (existingProductIndex !== -1) {
        // Le produit existe déjà, mettre à jour la quantité
        const existingItem = currentCommande.items[existingProductIndex];
        const newQuantity = existingItem.quantity + quantity;
    
        currentCommande.items[existingProductIndex] = {
          ...existingItem,
          quantity: newQuantity,
          total: existingItem.unitPrice * newQuantity,
          tva: (existingItem.unitPrice * newQuantity * existingItem.tauxTva) / 100,
          marge: (existingItem.unitPrice - existingItem.costPrice) * newQuantity
        };
    
        
      } else {
        // Nouveau produit, l'ajouter à la commande
        const costPrice = product.cost || product.price * 0.5; // Prix de revient par défaut
        const tauxTva = product.tauxTva || 20; // TVA par défaut
        const unitPrice = product.price;
        const total = unitPrice * quantity;
        const tva = (total * tauxTva) / 100;
        const marge = (unitPrice - costPrice) * quantity;
    
        const newLine = {
          productId: product.id,
          name: product.name,
          typeProduit: "product",
          unite: "piece",
          quantity: quantity,
          costPrice: costPrice,
          unitPrice: unitPrice,
          remisePercent: 0,
          discount: 0,
          tauxTva: tauxTva,
          tva: tva,
          total: total,
          marge: marge
        };
    
        currentCommande.items.push(newLine);
      }
    
      // Recalculer les montants
      updatedCommandes[currentCommandeIndex] = calculateMontants(currentCommande);
      return updatedCommandes;
    });
    
  };

  // Fonction pour calculer les montants de la commande
  const calculateMontants = (commande) => {
    const items = commande.items;
    const total = items.reduce((sum, item) => sum + item.total, 0);
    const discount = items.reduce((sum, item) => sum + item.discount, 0);
    const marge = items.reduce((sum, item) => sum + item.marge, 0);
    const tva = items.reduce((sum, item) => sum + item.tva, 0);
    const ht = total - tva;

    return {
      ...commande,
      montant: {
        total: total,
        discount: discount,
        marge: marge,
        tva: tva,
        ht: ht
      },
      updatedAt: new Date()
    };
  };

  // 5. Fonction pour mettre la commande courante en attente
  const saveCurrentCommande = () => {
    console.log('💾 Sauvegarde commande courante:', currentCommande);
    // TODO: Implémenter la logique
  };

  // 6. Fonction pour sauvegarder une vente complète
  const saveSale = async (saleData, paymentData = null) => {
    try {
      console.log('💾 Sauvegarde de la vente avec paiement:', { saleData, paymentData });
      
      // Créer une instance du service de vente
      const saleService = new SaleService();
      
      // Préparer les données de vente
      const completeSaleData = {
        ...saleData,
        date: new Date(),
        sens: 1, // Vente
        userId: currentUser?.id || null, // Utiliser l'ID de l'utilisateur connecté ou null
        lastSync: new Date()
      };

      // Utiliser la nouvelle méthode avec transaction atomique
      const result = await saleService.createSaleWithPayment(completeSaleData, paymentData);
      
      console.log('✅ Vente et paiement sauvegardés avec succès:', result);
      
      // Retourner les données de la vente sauvegardée
      return result;
      
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la vente:', error);
      throw error;
    }
  };

  // 7. Fonction pour gérer la finalisation du paiement
  const handlePaymentComplete = (paymentData) => {
    console.log('💳 Paiement finalisé:', paymentData);
    
    // Afficher une notification de succès
    const successMessage = `${t.notifications?.paymentSuccess || 'Paiement réussi !'}\nMontant: ${paymentData.amount.toFixed(2)} €\nMéthode: ${paymentData.method}\nMonnaie: ${paymentData.change.toFixed(2)} €`;
    
    setNotification({
      open: true,
      message: successMessage,
      type: 'success'
    });
    
    // TODO: Imprimer le ticket de caisse
    // TODO: Envoyer un SMS de confirmation si le téléphone est fourni
    // TODO: Mettre à jour les statistiques de vente
  };

   // 8. Fonction pour mettre à jour la quantité d'un item
  const updateItemQuantity = (index, newQuantity) => {    
    
    if (newQuantity <= 0) {
      // Supprimer l'item si quantité <= 0
      removeItemFromCommande(index);
      return;
    }

    setCommandes(prevCommandes => {
      // Copier le tableau des commandes
      const updatedCommandes = [...prevCommandes];
    
      // Cloner en profondeur la commande actuelle pour éviter toute mutation
      const oldCommande = updatedCommandes[currentCommandeIndex];
      const currentCommande = {
        ...oldCommande,
        items: [...oldCommande.items]
      };
    
      // Remplacer la commande par la copie dans le tableau
      updatedCommandes[currentCommandeIndex] = currentCommande;
    
      if (currentCommande.items[index]) {
        const item = currentCommande.items[index];
        currentCommande.items[index] = {
          ...item,
          quantity: newQuantity,
          total: item.unitPrice * newQuantity,
          tva: (item.unitPrice * newQuantity * item.tauxTva) / 100,
          marge: (item.unitPrice - item.costPrice) * newQuantity
        };
        console.log('✅ Quantité mise à jour pour 2:', item.name);
      }
      
      // Recalculer les montants
      updatedCommandes[currentCommandeIndex] = calculateMontants(currentCommande);
      return updatedCommandes;
    });
  };

  // 9. Fonction pour supprimer un item
  const removeItemFromCommande = (index) => {   
    
    setCommandes(prevCommandes => {
      // Copier le tableau des commandes
      const updatedCommandes = [...prevCommandes];
    
      // Cloner en profondeur la commande actuelle pour éviter toute mutation
      const oldCommande = updatedCommandes[currentCommandeIndex];
      const currentCommande = {
        ...oldCommande,
        items: [...oldCommande.items]
      };
    
      // Remplacer la commande par la copie dans le tableau
      updatedCommandes[currentCommandeIndex] = currentCommande;
    
      // Supprimer l'item à l'index spécifié
      currentCommande.items = currentCommande.items.filter((_, i) => i !== index);    
      
      // Recalculer les montants
      updatedCommandes[currentCommandeIndex] = calculateMontants(currentCommande);
      return updatedCommandes;
    });
    
    // Si on supprime la ligne sélectionnée, désélectionner
    if (selectedLineIndex === index) {
      setSelectedLineIndex(null);
    }
  };

  // 10. Fonction pour vider la commande courante
  const clearCurrentCommande = () => {    
    const newCommande = getInitialCommande();
    setCommandes(prev => {
      const updated = [...prev];
      updated[currentCommandeIndex] = newCommande;
      return updated;
    });
    setSelectedLineIndex(null);
  };

  // 11. Fonction pour créer une nouvelle commande
  const createNewCommande = () => {
   
    const newCommande = getInitialCommande();
    setCommandes(prev => [...prev, newCommande]);
    setCurrentCommandeIndex(prev => prev + 1);
    setSelectedLineIndex(null);
  };

  // 12. Fonction pour changer de commande active
  const switchToCommande = (index) => {
    
    if (commandes[index]) {
      setCurrentCommandeIndex(index);
      setSelectedLineIndex(null);
    }
  }; 

  useEffect(() => {
    setPageTitle(t.title || 'Point de Vente');
  }, [lang, t.title, setPageTitle]);

  // Log currentCommande à chaque modification
  useEffect(() => {
    console.clear();
    console.log('🛒 CurrentCommande mise à jour:', currentCommande);
  }, [currentCommande]);

  // Mapping des layouts
  const layouts = {
    retail: RetailLayout,
    restaurant: RestaurantLayout,
    services: ServicesLayout,
    workshop: WorkshopLayout
  };

  // Récupérer le composant de layout approprié
  const LayoutComponent = layouts[selectedLayout] || RetailLayout;

  // Props à passer aux layouts
  const commonProps = {
    currentCommande,
    selectedLineIndex,
    setSelectedLineIndex,
    addProductToCurrentCommande,
    updateItemQuantity,
    removeItemFromCommande,
    clearCurrentCommande,
    commandes,
    currentCommandeIndex,
    createNewCommande,
    switchToCommande,
    saveSale,
    handlePaymentComplete
  };

  return (
    <>
      <LayoutComponent {...commonProps} />
      
      {/* Notification de paiement */}
      <FloatingAlert
        open={notification.open}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        type={notification.type}
      >
        {notification.message}
      </FloatingAlert>
    </>
  );
};

export default Pos; 